'use client';

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import { fadeUp, viewportLazy } from '@/lib/motion';

const reasons = [
  {
    icon: 'BoltIcon',
    pill: 'Rapid delivery',
    title: 'Fast execution',
    description: 'From idea to launch in focused sprints, with a working demo every week.',
    points: ['Lean sprints, no ceremony', 'Quick decisions, not long meetings', 'Weekly demos you can click'],
  },
  {
    icon: 'CpuChipIcon',
    pill: 'AI powered',
    title: 'AI-first approach',
    description: 'We add AI where it improves the product, not to decorate it.',
    points: ['Smarter workflows', 'Faster day-to-day operations', 'Better customer experiences'],
  },
  {
    icon: 'PaintBrushIcon',
    pill: 'Human centered',
    title: 'Modern UI/UX',
    description: 'Interfaces that feel polished, fast and intuitive on every screen.',
    points: ['Polished, consistent visuals', 'Fast, smooth interactions', 'Clear flows your users understand'],
  },
  {
    icon: 'RocketLaunchIcon',
    pill: 'Growth mindset',
    title: 'Startup-focused',
    description: 'We know lean budgets and short timelines, and we build to iterate.',
    points: ['Scope that fits your budget', 'Timelines measured in weeks', 'Room to iterate and scale'],
  },
  {
    icon: 'CircleStackIcon',
    pill: 'Built to scale',
    title: 'Scalable architecture',
    description: 'Systems that are robust, flexible and future-ready, from MVP to enterprise.',
    points: ['MVP-ready from day one', 'Modular and easy to extend', 'Grows with your user base'],
  },
  {
    icon: 'MapIcon',
    pill: 'Outcome driven',
    title: 'Product thinking',
    description: 'We go beyond code: we solve the right problems and deliver real business outcomes.',
    points: ['Problem first, solution second', 'Every feature tied to an outcome', 'Impact you can measure'],
  },
] as const;

function SectionIntro() {
  return (
    <motion.div
      className="mb-10 text-center"
      initial="hidden"
      whileInView="visible"
      variants={fadeUp}
      viewport={viewportLazy}
    >
      <span className="section-label mb-3 block">Why CreatorTech</span>
      <h2 className="text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
        Built for speed.
        <br />
        <span className="gradient-text">Ready to scale.</span>
      </h2>
      <p className="mx-auto mt-4 max-w-lg text-lg text-foreground/50">
        We pair product judgment, AI-native delivery, and strong engineering foundations for teams
        that need to move now.
      </p>
    </motion.div>
  );
}

function SummaryBar() {
  return (
    <motion.div
      className="relative z-20 mt-8 overflow-hidden rounded-[1.5rem] border border-foreground/10 bg-white px-5 py-3 shadow-[0_12px_34px_-28px_rgba(15,23,42,0.45)]"
      initial="hidden"
      whileInView="visible"
      variants={fadeUp}
      viewport={viewportLazy}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon name="SparklesIcon" size={16} />
        </div>
        <p className="text-sm font-semibold leading-snug text-foreground/70 md:text-base">
          We combine <span className="text-primary">speed</span>, <span className="text-primary">AI</span>, and
          product thinking to turn ideas into <span className="text-primary">scalable digital products</span>.
        </p>
      </div>
    </motion.div>
  );
}

function Tick() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <polyline
        points="5 12.5 10 17.5 19 7"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function WhyChooseUsSection() {
  const stackRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const ul = stackRef.current;
    if (!ul) return;

    const cards = [...ul.querySelectorAll<HTMLElement>('.why-card')];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
    let enabled = false;
    let ticking = false;
    let base = 88;
    let step = 16;
    let heights: number[] = [];

    const setActive = (index: number) => {
      cards.forEach((card, i) => card.classList.toggle('is-active', i === index));
    };

    const update = () => {
      ticking = false;
      let active = 0;

      if (enabled) {
        cards.forEach((card, i) => {
          if (card.getBoundingClientRect().top <= base + i * step + 160) active = i;
        });
      } else {
        let best = Infinity;
        cards.forEach((card, i) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.abs((rect.top + rect.bottom) / 2 - window.innerHeight / 2);
          if (distance < best) {
            best = distance;
            active = i;
          }
        });
      }

      setActive(active);
      if (!enabled) return;

      cards.forEach((card, i) => {
        const next = cards[i + 1];
        if (!next) return;
        const height = heights[i];
        const progress = clamp(
          (base + i * step + height - next.getBoundingClientRect().top) / (height - step),
          0,
          1,
        );
        card.style.setProperty('--p', progress.toFixed(3));
      });
    };

    const measure = () => {
      const styles = getComputedStyle(ul);
      base = parseFloat(styles.getPropertyValue('--why-base')) || 88;
      step = parseFloat(styles.getPropertyValue('--why-step')) || 16;
      ul.classList.remove('is-stack');
      heights = cards.map((card) => card.offsetHeight);
      enabled = !reduce.matches;
      const lastHeight = heights[heights.length - 1];
      const release = lastHeight + base + step * (cards.length - 1) + 24 + 48 - 160;
      ul.style.setProperty('--tail', `${Math.max(24, window.innerHeight - release)}px`);
      ul.classList.toggle('is-stack', enabled);
      if (!enabled) cards.forEach((card) => card.style.removeProperty('--p'));
      update();
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const onReduce = () => measure();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', measure);
    reduce.addEventListener('change', onReduce);
    const ready = document.fonts?.ready ?? Promise.resolve();
    ready.then(measure);
    measure();

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', measure);
      reduce.removeEventListener('change', onReduce);
    };
  }, []);

  return (
    <section id="why-us" className="relative scroll-mt-28 bg-[#F4F6F8] py-14 lg:py-24">
      <div className="relative z-10 mx-auto max-w-[1215px] px-6 lg:px-8">
        <SectionIntro />

        <ul className="why-stack" ref={stackRef}>
          {reasons.map((reason, index) => (
            <li
              key={reason.title}
              className={`why-card${index === 0 ? ' is-active' : ''}`}
              style={{ ['--i' as string]: index, zIndex: index + 1 }}
            >
              <div className="why-left">
                <div className="why-top">
                  <span className="why-ico">
                    <Icon name={reason.icon} size={28} />
                  </span>
                  <span className="why-pill">{reason.pill}</span>
                </div>
                <div>
                  <h3>{reason.title}</h3>
                  <p className="why-desc">{reason.description}</p>
                </div>
              </div>
              <div className="why-panel">
                <ul className="why-how">
                  {reason.points.map((point) => (
                    <li key={point}>
                      <span className="why-tick">
                        <Tick />
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
          <li className="why-tail" aria-hidden="true" />
        </ul>

        <SummaryBar />
      </div>
    </section>
  );
}
